import { useMemo } from 'react'
import { Diagnosis } from './clinical/engine'
import { Episode, evaluateEpisode } from './clinical/episode'
import { evaluateTherapyGuidance } from './clinical/therapy'
import { getGuidedWorkflowPhase, getGuidedWorkflowSteps, guidedActionTarget } from './clinical/workflow'
import { RuleReferences } from './ReferenceLinks'

const diagnosisLabels: Record<Diagnosis, string> = {
  DKA: 'DKA',
  EUGLYCEMIC_DKA: 'DKA euglicemica',
  HHS: 'HHS',
  MIXED_DKA_HHS: 'Forma mista DKA/HHS',
  UNCLASSIFIED: 'Quadro non classificato'
}

const rateLabel = (value?: number, unit = '') => value === undefined ? '—' : `${value > 0 ? '+' : ''}${value}${unit}`
const ketoneLabel = (episode: Episode) => {
  const input = episode.observations[episode.observations.length - 1].input
  if (input.betaHydroxybutyrateMmolL !== undefined) return `${input.betaHydroxybutyrateMmolL} mmol/L β-OHB`
  if (input.urineKetones !== undefined) return input.urineKetones === 0 ? 'chetonuria negativa' : `${input.urineKetones}+ chetonuria`
  return 'non disponibili'
}

export default function GuidedWorkflowPanel({ episode }: { episode: Episode }) {
  const phase = getGuidedWorkflowPhase(episode)
  const steps = getGuidedWorkflowSteps(episode)
  const latest = episode.observations[episode.observations.length - 1]
  const evaluation = useMemo(() => evaluateEpisode(episode, new Date().toISOString()), [episode])
  const guidance = useMemo(() => evaluateTherapyGuidance(episode), [episode])
  const initialResult = episode.observations[0].result
  const diagnosis = episode.diagnosis === 'UNCLASSIFIED' && initialResult.provisionalPattern
    ? initialResult.provisionalPattern.label
    : diagnosisLabels[episode.diagnosis]
  const currentPhenotype = latest.result.diagnosis === 'UNCLASSIFIED'
    ? latest.result.provisionalPattern?.label ?? 'Crisi non più classificabile come attiva'
    : diagnosisLabels[latest.result.diagnosis]

  const openDetails = (target?: string) => {
    const details = document.getElementById('dettagli-episodio') as HTMLDetailsElement | null
    if (details) details.open = true
    const targetId = target ?? guidedActionTarget(phase)
    window.setTimeout(() => {
      const direct = document.getElementById(targetId)
      const fallback = phase === 'monitoring'
        ? document.querySelector('.reassessment')
        : phase === 'resolution'
          ? document.querySelector('.resolution-banner')
          : details
      ;(direct ?? fallback)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 80)
  }

  const potassiumReference = guidance.potassium.replacementRateMmolHour !== undefined
    ? `${guidance.potassium.replacementRateMmolHour} mmol/h · insulina standard rinviata`
    : guidance.potassium.fluidSupplementMmolL
      ? `${guidance.potassium.fluidSupplementMmolL.low}–${guidance.potassium.fluidSupplementMmolL.high} mmol/L nel fluido`
      : guidance.potassium.status === 'hold_supplement' ? 'nessuna supplementazione iniziale' : 'da valutare'

  const insulinReference = guidance.phase === 'resolution_pending'
    ? 'Nessun incremento automatico'
    : guidance.phase === 'insulin_blocked'
      ? 'Rinviata per K <3,5'
      : guidance.insulinUnitsHour !== undefined
        ? `${guidance.insulinUnitsHour} U/h · ${guidance.insulinUnitsKgHour} U/kg/h`
        : 'Non automatizzata'

  const fluidReference = guidance.fluid.referenceRateMlHour
    ? `${guidance.fluid.referenceRateMlHour.low}–${guidance.fluid.referenceRateMlHour.high} mL/h`
    : guidance.fluid.fluidReference ?? 'Individualizzare'

  const dueItems = [
    evaluation.monitoring.glucose === 'due' ? 'glicemia' : '',
    evaluation.monitoring.metabolicPanel === 'due' ? 'elettroliti / pH' : '',
    evaluation.monitoring.ketones === 'due' && episode.observations.some((o) => o.input.betaHydroxybutyrateMmolL !== undefined) ? 'β-OHB' : ''
  ].filter(Boolean)

  const actionLabel = phase === 'stabilization'
    ? 'Registra trattamento iniziale'
    : phase === 'monitoring'
      ? 'Inserisci nuova rivalutazione'
      : phase === 'resolution'
        ? 'Conferma la risoluzione'
        : episode.status === 'closed'
          ? 'Consulta dettagli episodio'
          : 'Completa transizione EV → SC'

  const referenceIds = phase === 'stabilization'
    ? ['fluids', 'potassium', 'insulin_iv'] as const
    : phase === 'monitoring'
      ? ['hhs_guardrails', 'ketones'] as const
      : phase === 'resolution'
        ? ['resolution', 'insulin_iv'] as const
        : ['transition'] as const

  return (
    <section className={`panel guided-workflow phase-${phase}`}>
      <div className="guided-heading">
        <div>
          <p className="step">Percorso guidato · v0.5</p>
          <h2>{diagnosis}</h2>
          <p>Quadro attuale: <strong>{currentPhenotype}</strong></p>
        </div>
        <span className="guided-phase-badge">{steps.find((step) => step.state === 'current')?.label ?? 'Completato'}</span>
      </div>

      <div className="workflow-stepper" aria-label="Fasi della gestione">
        {steps.map((step, index) => (
          <div className={`workflow-step ${step.state}`} key={step.id} aria-current={step.state === 'current' ? 'step' : undefined}>
            <span>{step.state === 'completed' ? '✓' : index + 1}</span>
            <strong>{step.label}</strong>
          </div>
        ))}
      </div>

      <div className="guided-now">
        <p className="guided-kicker">Cosa richiede attenzione adesso</p>

        {phase === 'stabilization' && (
          <>
            <h3>{guidance.title}</h3>
            <p>{guidance.message}</p>
            <div className="guided-action-grid">
              <div><span>Fluidi</span><strong>{fluidReference}</strong><small>{guidance.fluid.title}</small></div>
              <div><span>Potassio</span><strong>{latest.input.potassiumMmolL ?? '—'} mmol/L</strong><small>{potassiumReference}</small></div>
              <div><span>Insulina EV</span><strong>{insulinReference}</strong><small>{guidance.dextrosePhase ? 'associare glucosio EV' : 'riferimento del motore'}</small></div>
            </div>
          </>
        )}

        {phase === 'monitoring' && (
          <>
            <h3>Valuta la risposta e decidi se modificare la terapia</h3>
            <div className="guided-monitor-grid">
              <div><span>Glicemia</span><strong>{latest.input.glucoseMgDl ?? '—'}</strong><small>{rateLabel(evaluation.rates.glucoseMgDlHour, ' mg/dL/h')}</small></div>
              <div><span>K</span><strong>{latest.input.potassiumMmolL ?? '—'}</strong><small>mmol/L</small></div>
              <div><span>Chetoni</span><strong>{ketoneLabel(episode)}</strong><small>{rateLabel(evaluation.rates.betaHydroxybutyrateHour, ' mmol/L/h')}</small></div>
              <div><span>pH / HCO₃⁻</span><strong>{latest.input.ph ?? '—'} / {latest.input.bicarbonateMmolL ?? '—'}</strong><small>risposta acido-base</small></div>
            </div>
            {dueItems.length > 0
              ? <div className="guided-next-check warning"><strong>Controlli dovuti:</strong> {dueItems.join(' · ')}</div>
              : <div className="guided-next-check"><strong>Monitoraggio in corso.</strong> Inserire i nuovi dati quando disponibili; il motore ricalcola trend, alert e snodi terapeutici.</div>}
            {evaluation.longitudinalAlerts.slice(0, 2).map((alert) => <div className={`guided-inline-alert ${alert.level}`} key={alert.id}><strong>{alert.title}</strong><span>{alert.message}</span></div>)}
          </>
        )}

        {phase === 'resolution' && (
          <>
            <h3>Criteri compatibili con risoluzione raggiunti</h3>
            <p>Non viene proposta alcuna risalita automatica alla dose insulinica iniziale. Confermare clinicamente la risoluzione prima di passare alla transizione.</p>
            <div className="guided-resolution-grid">
              <div><span>β-OHB / chetoni</span><strong>{ketoneLabel(episode)}</strong></div>
              <div><span>pH</span><strong>{latest.input.ph ?? '—'}</strong></div>
              <div><span>HCO₃⁻</span><strong>{latest.input.bicarbonateMmolL ?? '—'} mmol/L</strong></div>
              <div><span>Glicemia</span><strong>{latest.input.glucoseMgDl ?? '—'} mg/dL</strong></div>
            </div>
            <div className="guided-next-check success"><strong>Insulina EV:</strong> nessun incremento automatico. Valutare conferma di risoluzione e pianificazione dell’overlap EV → SC.</div>
          </>
        )}

        {phase === 'transition' && (
          <>
            <h3>{episode.status === 'closed' ? 'Episodio concluso' : 'Passaggio dall’insulina EV alla terapia sottocutanea'}</h3>
            <p>{episode.status === 'closed' ? 'La gestione è stata chiusa. Timeline, audit e fonti restano consultabili nei dettagli.' : 'La crisi è stata confermata come risolta. Ora il focus è TDD, quota basale, alimentazione, overlap e stop dell’infusione EV.'}</p>
            {episode.status !== 'closed' && <div className="guided-next-check"><strong>Overlap standard:</strong> basale SC almeno 2 h prima della sospensione dell’insulina EV.</div>}
          </>
        )}

        <div className="guided-actions">
          <button className="primary" type="button" onClick={() => openDetails()}>{actionLabel}</button>
          <button className="secondary" type="button" onClick={() => openDetails('dettagli-episodio')}>Dettagli episodio</button>
        </div>
        <RuleReferences ids={[...referenceIds]} label="Fonte clinica" />
      </div>
    </section>
  )
}
